import React, { useContext, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { ToastContext } from "../../../../App";
import {
  useGetOrganizationListQuery,
  useUpdateOrganizationMutation,
} from "../../../../redux/services/organization";
import { timezoneOptions } from "../../../../utils/timezoneOptions";

import * as styles from "../Organizations.module.css";

const LIST_PATH = "/app/admin/organizations";

function OrganizationEditPage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: organizations } = useGetOrganizationListQuery();

  const organization = useMemo(
    () => organizations?.find((o) => String(o.id) === String(id)),
    [organizations, id],
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { name: "", subdomain: "", timezone: "" },
  });

  useEffect(() => {
    if (organization) {
      reset({
        name: organization.name || "",
        subdomain: organization.subdomain || "",
        timezone: organization.timezone || "",
      });
    }
  }, [organization, reset]);

  const [
    updateOrganization,
    {
      isSuccess: isUpdateOrganizationSuccess,
      isError: isUpdateOrganizationError,
      error: updateOrganizationError,
    },
  ] = useUpdateOrganizationMutation();

  useEffect(() => {
    if (isUpdateOrganizationSuccess) {
      showToast("success", "Organization", "Organization updated!");
      navigate(LIST_PATH);
    }
  }, [isUpdateOrganizationSuccess]);

  useEffect(() => {
    if (isUpdateOrganizationError) {
      showToast(
        "error",
        updateOrganizationError?.status,
        updateOrganizationError?.data?.message ||
          (updateOrganizationError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateOrganizationError]);

  const onSaveEdit = (data) => {
    updateOrganization({
      id,
      payload: {
        name: data.name,
        subdomain: data.subdomain,
        timezone: data.timezone,
      },
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(onSaveEdit)();
    }
  };

  const title = organization?.name
    ? `Edit Organization — ${organization.name}`
    : "Edit Organization";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onSaveEdit)}
        />
      }
    >
      <form onSubmit={handleSubmit(onSaveEdit)}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Name</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                onKeyDown={handleKeyDown}
                placeholder="Name"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="subdomain"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Subdomain</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                onKeyDown={handleKeyDown}
                placeholder="Subdomain"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="timezone"
          control={control}
          render={({ field }) => (
            <div className={`field ${styles.dropDownWrapper}`}>
              <label>Timezone</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select timezone"
                className="w-full"
                panelClassName={styles.dropDownPanel}
                filter
                options={timezoneOptions}
              />
            </div>
          )}
        />

        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default OrganizationEditPage;
